/**
 * TestPage - Container component for API testing
 *
 * Manages test data and delegates UI to TestPageView.
 * Handles preset endpoint selection, test execution, and result management.
 */
import { useState, useCallback, type FormEvent } from 'react';
import TestPageView from './TestPageView';
import { executeTest } from './testing.api';
import type { TestFormData, TestResult } from './testing.schema';

interface TestPageProps {
  userId: string;
  setUserId: (userId: string) => void;
}

// Preset endpoints matching the actual registered routes — grouped by feature
export interface PresetEndpoint {
  group: string;   // optgroup label in the selector
  label: string;   // human-readable option label
  method: TestFormData['method'];
  endpoint: string; // may contain USER_ID / EVENT_ID / KEY_ID placeholders
  body?: string;    // sample JSON body; omit for GET/DELETE
}

// Placeholder tokens replaced with current userId when a preset is loaded
const USER_TOKEN = 'USER_ID';
const EVENT_TOKEN = 'EVENT_ID';
const KEY_TOKEN = 'KEY_ID';

export const PRESET_ENDPOINTS: PresetEndpoint[] = [
  // ── Health ────────────────────────────────────────────────────────────────
  { group: 'Health', label: 'Basic health check',           method: 'GET',    endpoint: '/health' },
  { group: 'Health', label: 'Detailed system status',       method: 'GET',    endpoint: '/health/detailed' },
  { group: 'Health', label: 'Database connectivity',        method: 'GET',    endpoint: '/health/database' },
  { group: 'Health', label: 'Readiness probe',              method: 'GET',    endpoint: '/health/readiness' },
  { group: 'Health', label: 'Liveness probe',               method: 'GET',    endpoint: '/health/liveness' },
  // ── API Keys ──────────────────────────────────────────────────────────────
  { group: 'API Keys', label: 'List API keys',              method: 'GET',    endpoint: '/api/keys' },
  { group: 'API Keys', label: 'Create API key',             method: 'POST',   endpoint: '/api/keys',          body: '{"label":"test-key"}' },
  { group: 'API Keys', label: 'Revoke API key',             method: 'DELETE', endpoint: `/api/keys/${KEY_TOKEN}` },
  // ── Credits ───────────────────────────────────────────────────────────────
  { group: 'Credits', label: 'Get balance',                 method: 'GET',    endpoint: `/api/credits/${USER_TOKEN}` },
  { group: 'Credits', label: 'Get balance (alias)',         method: 'GET',    endpoint: `/api/credits/balance/${USER_TOKEN}` },
  { group: 'Credits', label: 'Allocate credits',            method: 'POST',   endpoint: '/api/credits/allocate', body: `{"userId":"${USER_TOKEN}","amount":50}` },
  { group: 'Credits', label: 'Consume credits',             method: 'POST',   endpoint: '/api/credits/consume',  body: `{"userId":"${USER_TOKEN}","amount":10,"operationId":"op_abc123"}` },
  // ── Payments ──────────────────────────────────────────────────────────────
  { group: 'Payments', label: 'Get client token',           method: 'GET',    endpoint: '/api/payments/client-token' },
  { group: 'Payments', label: 'Create one-time payment',    method: 'POST',   endpoint: '/api/payments/create',  body: `{"userId":"${USER_TOKEN}","nonce":"fake-valid-nonce","amount":50}` },
  { group: 'Payments', label: 'Create payment (alias)',     method: 'POST',   endpoint: '/api/payments',          body: `{"userId":"${USER_TOKEN}","nonce":"fake-valid-nonce","amount":50}` },
  { group: 'Payments', label: 'Billing history (NDJSON)',   method: 'GET',    endpoint: `/api/payments/subscription/billing/${USER_TOKEN}?limit=20` },
  { group: 'Payments', label: 'Test payment sandbox',       method: 'GET',    endpoint: '/test-payment' },
  // ── Subscriptions ─────────────────────────────────────────────────────────
  { group: 'Subscriptions', label: 'Create subscription',  method: 'POST',   endpoint: '/api/subscriptions/create',               body: `{"userId":"${USER_TOKEN}","planId":"qredit_50_monthly","nonce":"fake-valid-nonce"}` },
  { group: 'Subscriptions', label: 'Get subscription',     method: 'GET',    endpoint: `/api/subscriptions/${USER_TOKEN}` },
  { group: 'Subscriptions', label: 'Change plan',          method: 'PUT',    endpoint: `/api/subscriptions/${USER_TOKEN}/plan`,      body: '{"planId":"qredit_100_monthly"}' },
  { group: 'Subscriptions', label: 'Cancel subscription',  method: 'POST',   endpoint: `/api/subscriptions/${USER_TOKEN}/cancel` },
  { group: 'Subscriptions', label: 'Delete subscription',  method: 'DELETE', endpoint: `/api/subscriptions/${USER_TOKEN}` },
  { group: 'Subscriptions', label: 'Add credit pack',      method: 'POST',   endpoint: `/api/subscriptions/${USER_TOKEN}/credit-pack`, body: '{"packId":"pack_50"}' },
  { group: 'Subscriptions', label: 'Replace credit pack',  method: 'PUT',    endpoint: `/api/subscriptions/${USER_TOKEN}/credit-pack`, body: '{"packId":"pack_100"}' },
  { group: 'Subscriptions', label: 'Toggle auto-renew',    method: 'POST',   endpoint: `/api/subscriptions/${USER_TOKEN}/auto-renew`, body: '{"enabled":true}' },
  // ── Webhooks ──────────────────────────────────────────────────────────────
  { group: 'Webhooks', label: 'Braintree verification',    method: 'GET',    endpoint: '/api/webhooks/braintree' },
  { group: 'Webhooks', label: 'Braintree receiver',        method: 'POST',   endpoint: '/api/webhooks/braintree',  body: '{"bt_signature":"...","bt_payload":"..."}' },
  { group: 'Webhooks', label: 'Braintree receiver (alias)',method: 'POST',   endpoint: '/api/braintree-webhook',   body: '{"bt_signature":"...","bt_payload":"..."}' },
  { group: 'Webhooks', label: 'Event status',              method: 'GET',    endpoint: `/webhooks/status/${EVENT_TOKEN}` },
  { group: 'Webhooks', label: 'List events (admin)',        method: 'GET',    endpoint: '/webhooks/admin/events?page=1&limit=20' },
  { group: 'Webhooks', label: 'Get single event (admin)',   method: 'GET',    endpoint: `/webhooks/admin/events/${EVENT_TOKEN}` },
  { group: 'Webhooks', label: 'Retry event (admin)',        method: 'POST',   endpoint: `/webhooks/admin/events/${EVENT_TOKEN}/retry` },
  { group: 'Webhooks', label: 'Bulk retry (admin)',         method: 'POST',   endpoint: '/webhooks/admin/events/retry-bulk',         body: `{"ids":["${EVENT_TOKEN}_1","${EVENT_TOKEN}_2"]}` },
  // ── Users ─────────────────────────────────────────────────────────────────
  { group: 'Users', label: 'Get payment provider',         method: 'GET',    endpoint: `/api/users/${USER_TOKEN}/payment-mode` },
  { group: 'Users', label: 'Set payment provider',         method: 'PATCH',  endpoint: `/api/users/${USER_TOKEN}/payment-mode`,    body: '{"paymentMode":"creem","reason":"user preference"}' },
  // ── Analytics ─────────────────────────────────────────────────────────────
  { group: 'Analytics', label: 'KPIs',                     method: 'GET',    endpoint: '/api/analytics/kpis' },
  { group: 'Analytics', label: 'Payment metrics',          method: 'GET',    endpoint: '/api/analytics/payments?startDate=2026-01-01&endDate=2026-04-01' },
  { group: 'Analytics', label: 'Subscription metrics',     method: 'GET',    endpoint: '/api/analytics/subscriptions' },
  { group: 'Analytics', label: 'Webhook metrics',          method: 'GET',    endpoint: '/api/analytics/webhooks?startDate=2026-01-01&endDate=2026-04-01' },
  { group: 'Analytics', label: 'Custom report',            method: 'POST',   endpoint: '/api/analytics/custom',                   body: '{"metrics":["revenue"],"startDate":"2026-01-01","endDate":"2026-04-01"}' },
];

const TestPage=({userId,setUserId}:TestPageProps)=>{
    console.log(`TestPage is running with userId: ${userId}`);
    const[testResult,setTestResult]=useState<TestResult|null>(null);
    const[loading,setLoading]=useState(false);
    const[endpoint,setEndpoint]=useState('/api/credits/'+userId); // default to a real endpoint
    const[method,setMethod]=useState<TestFormData['method']>('GET'); // GET is safe as default
    const[headers,setHeaders]=useState('{"X-API-Key":"your-api-key"}'); // correct auth header
    const[body,setBody]=useState('{}');

    // Load a preset: substitute USER_ID/EVENT_ID/KEY_ID tokens with the current userId value
    const handlePresetSelect=useCallback((preset:PresetEndpoint)=>{
        const sub=(s:string)=>s
            .replace(new RegExp(USER_TOKEN,'g'),userId||USER_TOKEN) // replace USER_ID with real value
            .replace(new RegExp(EVENT_TOKEN,'g'),EVENT_TOKEN)       // leave event/key tokens visible
            .replace(new RegExp(KEY_TOKEN,'g'),KEY_TOKEN);
        setEndpoint(sub(preset.endpoint));
        setMethod(preset.method);
        setBody(preset.body?sub(preset.body):'{}'); // pre-fill sample body when available
        setTestResult(null); // clear stale result when switching presets
    },[userId]);

    const handleTestSubmit=useCallback(async(testData:TestFormData)=>{
        setLoading(true);
        try{
            const result=await executeTest(testData);
            setTestResult(result);
        }catch(error){
            setTestResult({success:false,status:0,data:null,error:error instanceof Error?error.message:'Unknown error',timestamp:Date.now()});
        }finally{
            setLoading(false);
        }
    },[]);

    const handleFormSubmit=useCallback(async(event:FormEvent<HTMLFormElement>)=>{
        event.preventDefault();
        let parsedHeaders:Record<string,string>;
        try{
            parsedHeaders=headers.trim()?JSON.parse(headers):{};
        }catch(error){
            setTestResult({success:false,status:0,data:null,error:'Headers must be valid JSON',timestamp:Date.now()});
            return;
        }
        await handleTestSubmit({endpoint,method,headers:parsedHeaders,body});
    },[body,endpoint,handleTestSubmit,headers,method]);

    console.log('TestPage is returning test page');
    return(
        <TestPageView
            userId={userId}
            onUserIdChange={setUserId}
            endpoint={endpoint}
            onEndpointChange={setEndpoint}
            method={method}
            onMethodChange={(v)=>setMethod(v as TestFormData['method'])}
            headers={headers}
            onHeadersChange={setHeaders}
            body={body}
            onBodyChange={setBody}
            testResult={testResult}
            loading={loading}
            onFormSubmit={handleFormSubmit}
            presets={PRESET_ENDPOINTS}
            onPresetSelect={handlePresetSelect}
        />
    );
};

export default TestPage;
